import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormState } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { WizardStep } from './consts';
import Button from '../../../../../components/atoms/Button';
import { ButtonMode } from '../../../../../components/atoms/Button/enums';
import PriceWithCurrency from '../../../../../components/atoms/PriceWithCurrency';
import routes from '../../../../../setup/consts/routes';
import { useFeesProfileData } from '../OrderForm/hooks';
import { OrderFormValues } from '../../types';
import {
  StyledFooter,
  StyledFooterTotals,
  StyledFooterDeliveryLine,
  StyledFooterGrandTotal,
  StyledFooterNav,
} from './styles';

interface WizardFooterProps {
  step: WizardStep;
  isLast: boolean;
  isEdit: boolean;
  loading?: boolean;
  onBack: () => void;
  onNext: () => void;
}

const WizardFooter: React.FC<WizardFooterProps> = ({
  step,
  isLast,
  isEdit,
  loading,
  onBack,
  onNext,
}) => {
  const history = useHistory();
  const { values } = useFormState<OrderFormValues>();
  const { deliveryFee, currency: profileCurrency } = useFeesProfileData();

  const displayCurrency = values.currency ?? profileCurrency;
  // Same math as the retired OrderApprovalBar: the fee is added on top
  // unless the entered amount already includes the delivery charge.
  const grandTotal =
    (values.total ?? 0) +
    (values.amountIncludeDeliveryCharge ? 0 : deliveryFee ?? 0);

  return (
    <StyledFooter>
      <Button
        type='button'
        mode={ButtonMode.Secondary}
        disabled={loading}
        onClick={() => history.push(routes.ORDERS)}
      >
        <FormattedMessage id='forms.buttons.cancel' defaultMessage='Cancel' />
      </Button>
      <StyledFooterTotals data-tour='wizard-total'>
        <StyledFooterDeliveryLine>
          <FormattedMessage
            id='delivery.deliveryCharge'
            defaultMessage='Delivery charge'
          />
          <PriceWithCurrency price={deliveryFee} currency={displayCurrency} />
        </StyledFooterDeliveryLine>
        <StyledFooterGrandTotal>
          <FormattedMessage
            id='products.grandTotal'
            defaultMessage='Grand Total'
          />
          <PriceWithCurrency price={grandTotal} currency={displayCurrency} />
        </StyledFooterGrandTotal>
      </StyledFooterTotals>
      <StyledFooterNav>
        {step !== WizardStep.StoreCustomer && (
          <Button
            type='button'
            mode={ButtonMode.Secondary}
            disabled={loading}
            onClick={onBack}
          >
            <FormattedMessage id='forms.buttons.back' defaultMessage='Back' />
          </Button>
        )}
        {/* Distinct keys force React to swap DOM nodes between Next and the
            submit button. Without them, React morphs the same <button> from
            type='button' to type='submit' during the Next click, and the
            browser's default action for that very click submits the form. */}
        {isLast ? (
          <Button key='wizard-submit' type='submit' disabled={loading}>
            {isEdit ? (
              <FormattedMessage
                id='orders.saveOrder'
                defaultMessage='Save order'
              />
            ) : (
              <FormattedMessage
                id='orders.placeOrderForApproval'
                defaultMessage='Place order for approval'
              />
            )}
          </Button>
        ) : (
          <Button
            key='wizard-next'
            type='button'
            disabled={loading}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              onNext();
            }}
          >
            <FormattedMessage id='forms.buttons.next' defaultMessage='Next' />
          </Button>
        )}
      </StyledFooterNav>
    </StyledFooter>
  );
};

export default WizardFooter;
